/** The server's typed error code for a name already taken within the domain. */
const DUPLICATE_SPACE_NAME_RE = /DUPLICATE_SPACE_NAME/;

/** Whether `error` is the server's rejection for a name already taken in this domain. */
export function isDuplicateSpaceNameError(error: unknown): boolean {
  return error instanceof Error && DUPLICATE_SPACE_NAME_RE.test(error.message);
}

/**
 * The server's refusal to leave a client-scoped space without its client
 * attribute assigned and Required (DV-683).
 *
 * Matched on this narrow code, NOT on the `VALIDATION_ERROR` that rides in the
 * error's `extensions.code`. That one is shared by every space invariant — a
 * non-integer client id, `addClientIds` against an all-clients space (DV-500),
 * a client reference outside the space's scope (DV-675) — so branching on it
 * would point failures with nothing to do with the schema at the attribute
 * list. The narrow code is prefixed onto the MESSAGE server-side precisely so a
 * client reading only `message` can tell them apart, which is the same contract
 * `DUPLICATE_SPACE_NAME` above and `DocumentTypeSettings/errors.ts` rely on.
 */
const CLIENT_ID_REQUIRED_RE = /CLIENT_ID_REQUIRED/;

/** Whether the server refused the space's client-attribute assignment (DV-683). */
export function isClientIdRequiredError(error: unknown): boolean {
  return error instanceof Error && CLIENT_ID_REQUIRED_RE.test(error.message);
}

/**
 * The inline message for that refusal, or `null` when `error` is something else
 * — a caller returning `null` should fall back to its generic reporting (the
 * route's save toast) rather than swallowing the failure.
 *
 * Deliberately does NOT echo the server's sentence, which names the machine
 * slug (`'client_id'`). The user is looking at a grid row labelled with the
 * attribute's display name, so `attributeName` is passed in from the catalog —
 * same reasoning as `attributeNameConflictErrorMessage`.
 */
export function clientIdRequiredErrorMessage(
  attributeName: string,
  error: unknown,
): string | null {
  if (!isClientIdRequiredError(error)) return null;
  return `The server refused this change: a space scoped to one or more clients must keep ${attributeName} assigned and Required.`;
}
